import {
  ACESFilmicToneMapping,
  AmbientLight,
  Color,
  DirectionalLight,
  HalfFloatType,
  Material,
  Mesh,
  MeshBasicMaterial,
  MeshPhysicalMaterial,
  MeshStandardMaterial,
  Object3D,
  PCFSoftShadowMap,
  PerspectiveCamera,
  Scene,
  ShaderMaterial,
  SRGBColorSpace,
  WebGLRenderer,
  WebGLRenderTarget,
} from "three";
import { 
  EffectComposer, 
  EffectPass, 
  RenderPass,
  BlendFunction,
	SelectiveBloomEffect,
	SMAAEffect,
  ToneMappingMode,
  ToneMappingEffect,
  SMAAPreset,
  
} from "postprocessing";
import {N8AOPostPass} from 'n8ao';
import gsap from "gsap";

import { APP_EVENTS, EventsManager } from "../../events/EventsManager";
import { BaseScene } from "./BaseScene";
import { environment } from "./environment";
import { delay } from "../utils/time";
import { ScenesManager } from "../ScenesManager";
import { Car } from "../objects/Car";

import { Background } from "../objects/Background";
import { CameraManager } from "../CameraManager";
import { EnvironmentManager } from "../EnvironmentManager";
import { Interior } from "../objects/Interior";
import { isDesktop, isMobile } from "utils/platform";
import { getFXScene } from "./transition/FXScene";
import { getTransition } from "./transition/Transition";
import { prepareTransition } from "../objects/Car.parse";
import { SpriteManager } from "../objects/SpriteManager";

class StandardScene extends BaseScene {
  public renderer: WebGLRenderer;
  public environment!: WebGLRenderTarget;
  private carObject!: Car;
  private interior!: Interior;
  private backgroundObject!: Background;
  bloomTween!: gsap.core.Tween;
  aspectRatio = 2;

  hasObject = false;
  
  renderTarget: any;
  materials: {[uuid: string]: Material | Material[]} = {};
  darkMaterial = new MeshBasicMaterial( { color: 'black' } );

  composer!: EffectComposer;
  bloom!: SelectiveBloomEffect;

  constructor(
    readonly canvas: HTMLCanvasElement,
    readonly onStarted: () => void,
    readonly onUpdate: () => void
  ) {
    super(canvas);

    this.aspectRatio = isDesktop() ? window.devicePixelRatio * 1.25 : window.devicePixelRatio;    
    
    this.canvas = canvas;
    this.renderer = new WebGLRenderer({
      canvas: canvas,
      alpha: true,
      powerPreference: "high-performance",
      stencil: false,
	    depth: false
    });
    this.renderer.setPixelRatio(this.aspectRatio);
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.outputColorSpace = SRGBColorSpace;
    this.renderer.toneMapping = ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.5;

    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = PCFSoftShadowMap;

    this.setStandardSceneDPR();

    this.environment = environment(this.renderer);
    this.scene.environment = this.environment.texture;
    this.camera = new PerspectiveCamera(
      28,
      window.innerWidth / window.innerHeight,
      0.001,
      10000
    );
    this.setComposer();

    CameraManager.setup(this.camera, this.canvas);

    this.scene.background = new Color(0x000000)

    this.selectedObjects = [];
    

    this.container = new Object3D();
    this.scene.add(this.container);


    // EnvironmentManager.setup(this.scene);

    // this.build();

   
  }

  setComposer() {
    this.composer = new EffectComposer(this.renderer, {
      frameBufferType: HalfFloatType
    })
    const smaaEffect = new SMAAEffect(
      {
        preset: SMAAPreset.ULTRA
      }
    );
		//smaaEffect.edgeDetectionMaterial.edgeDetectionThreshold = 0.1;

		this.bloom = new SelectiveBloomEffect(this.scene, this.camera, {
			blendFunction: BlendFunction.ADD,
			mipmapBlur: true,
			luminanceThreshold: 0.4,
			luminanceSmoothing: 0.2,
			intensity: 0.5
		});
    this.bloom.inverted = true;
    // this.bloom.ignoreBackground = true;
    // this.bloom.luminancePass.enabled = false;
		this.bloom.inverted = true;

    if (ScenesManager.tweakPane) {        
      const bloomF = ScenesManager.tweakPane.addFolder({
        title: "Bloom",
        expanded: false,
      });
      bloomF.addBinding(this.bloom, "intensity", {
        min: 0.0, 
        max: 10.0,
        step: 0.01
      });
  
      bloomF.addBinding(this.bloom.mipmapBlurPass, "radius", {
        min: 0.0, 
        max: 1.0, 
        step:0.001
      });
    }


    const toneMappingEffect = new ToneMappingEffect({
			mode: ToneMappingMode.NEUTRAL,
			resolution: 256,
			whitePoint: 20.0, // increased from 12.0 to allow more brightness
			middleGrey: 1,  // increased from 0.8 to brighten mid-tones
			minLuminance: 0.05, // increased from 0.01 to prevent blacks from being too dark
			averageLuminance: 0.1, // increased from 0.01 for better brightness balance
			adaptationRate: 1.0

      // resolution: 256,
			// whitePoint: 16.0,
			// middleGrey: 0.6,
			// minLuminance: 0.01,
			// averageLuminance: 0.01,
			// adaptationRate: 1.0
		});

    // const n8aopass = new N8AOPostPass(this.scene, this.camera, window.innerWidth, window.innerHeight);
    // n8aopass.configuration.aoRadius = 5.0;
    // n8aopass.configuration.distanceFalloff = 1.0;
    // n8aopass.configuration.intensity = 3.;
    
    this.composer.addPass(new RenderPass(this.scene, this.camera));
    
    if (ScenesManager.gpuTier && (ScenesManager.gpuTier.tier >= 2)){
      this.composer.addPass(new EffectPass(this.camera, smaaEffect));
      // this.composer.addPass(n8aopass);
    }
    this.composer.addPass(new EffectPass(this.camera, toneMappingEffect, this.bloom));

  }

  public setStandardSceneDPR() {
    if (isMobile()) {
      const DPR = Math.min(window.devicePixelRatio, 2);
      this.renderer.setPixelRatio(DPR);
    }
    this.resize();
  }

  async build() {
    if (this.carObject) return;

    const light = new DirectionalLight( 0xf0f6ff, 1 );
    ScenesManager.directionalLight = light;
    light.position.set( 2, 8, 4 );
    light.castShadow = true;
    light.shadow.mapSize.width = 1024;
    light.shadow.mapSize.height = 1024;
    light.shadow.camera.far = 20;
    this.scene.add( light );   

    this.carObject = new Car();
    this.container?.add(this.carObject);


    // this.createTransitionView()
   
    this.interior = new Interior();
    this.container?.add(this.interior);

    this.backgroundObject = new Background();
    this.backgroundObject.rotation.set(0, Math.PI / 2, 0);
    this.container?.add(this.backgroundObject);

    EventsManager.instance.on(APP_EVENTS.APP_HIDE, () => {
      this.carObject?.hide();
      ScenesManager.inTransition = false;
      ScenesManager.followMouseEnabled = true;
    });
    EventsManager.instance.on(APP_EVENTS.APP_SHOW, () => {
      ScenesManager.inTransition = false;
      ScenesManager.followMouseEnabled = false;
      this.toggleBloom();
    });
    EventsManager.instance.on(APP_EVENTS.OUTLINE_SHOW, () => {
      this.carObject?.show();
    });
    EventsManager.instance.on(APP_EVENTS.INTERIOR_SHOW, () => {
      CameraManager.goToInterior(async () => {
        this.interior?.show();
        this.carObject.model.scale.set(0,0,0);
        this.carObject.seats.scale.set(0,0,0);
        this.carObject.outterContainer.visible = false;
        this.carObject.innerContainer.scale.set(1,1,1);
        this.backgroundObject.hideForInterior();
        this.showHideParticles(1);
        await delay(0.5);
        SpriteManager.inInterior = true;
      });
    });
    EventsManager.instance.on(APP_EVENTS.INTERIOR_HIDE, () => {
      SpriteManager.inInterior = false;
      this.interior?.hide();
      this.carObject.model.scale.set(1,1,1);
      this.carObject.seats.scale.set(1,1,1);
      this.carObject.outterContainer.visible = true;
      this.carObject.innerContainer.scale.set(0,0,0);
      this.backgroundObject.show();
      CameraManager.goFromInterior()
      this.showHideParticles(0);

      if (ScenesManager.EXTERNAL_REVEALEAD && !ScenesManager.INTERNAL_REVEALEAD) {
        for (let key in ScenesManager.featureTravelingParticles) {
          if (ScenesManager.featureTravelingParticles[key]) {
            ScenesManager.featureTravelingParticles[key].targets.forEach(item => {
                gsap.to((item.material as ShaderMaterial).uniforms.uOpacity, {
                  value: .0,
                  ease: "quad.inOut",
                  duration: 0.1,
                  onStartt: () => {
                    item.visible = false
                  }
                });
            })
          }
        }
      }
    });
    await delay(1);
    this.hasObject = true;

    this.renderer.setAnimationLoop(() => {
      this.onUpdate();
    });

    this.onStarted && this.onStarted();
  }


  showHideParticles(alpha: number) {
    if (ScenesManager.INTERNAL_REVEALEAD) return;
    
    for (let key in ScenesManager.featureTravelingParticles) {
      if (ScenesManager.featureTravelingParticles[key]) {
        ScenesManager.featureTravelingParticles[key].targets.forEach(item => {
          const shouldAnimate = item.userData.isInterior;
          if (shouldAnimate && alpha === 1 && !item.visible) {
            item.visible = true;
          }
          gsap.to((item.material as ShaderMaterial).uniforms.uOpacity, {
            value: shouldAnimate ? alpha : 1,
            ease: "quad.inOut",
            duration: alpha > 0 ? 1.4 : 0,
          });
        })
      }
    }
  }

  toggleBloom() {
    if (this.bloom) {
      this.bloomTween?.kill();
      this.bloomTween = gsap.to(this.bloom, {
			  intensity: 0.66,
			  radius: 0.543,
        duration: 2
      });
    }
  }

  readonly pause = () => {
    // console.log("StandardScene pause");
  };

  readonly resume = () => {
    // console.log("StandardScene resume");
  };

  readonly stop = () => {
    // console.log("StandardScene stop");
  };

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  readonly load = () => {};
  readonly start = () => {};

  readonly animate = () => {
    const delta = ScenesManager.clock.getDelta();
    CameraManager.update(delta);
    EnvironmentManager.update();
    ScenesManager.sprites.update()

    if (this.hasObject) {
      this.carObject?.animate();
      this.backgroundObject?.animate();
    }


    
    // if (this.postProcess?.composer) {
      //   this.scene.traverse( (obj: Object3D) => this.darkenNonBloomed(obj) );
      //   this.postProcess.bloomComposer.render();
      //   this.scene.traverse( (obj: Object3D) => this.restoreMaterial(obj) );
      
      //   this.postProcess.composer.render();
      // } else {
        //   this.renderer.render(this.scene, this.camera);
        // }
        
      this.composer?.render();
      this.transition?.render(delta);
  };

  darkenNonBloomed( obj: Object3D) {
    if ( (obj as Mesh).isMesh && ScenesManager.bloomLayer.test( obj.layers ) === false ) {
      this.materials[ obj.uuid ] = (obj as Mesh).material;
      (obj as Mesh).material = this.darkMaterial;
    }
  }

  restoreMaterial( obj: Object3D ) {
    if ( this.materials[ obj.uuid ] ) {
      (obj as Mesh).material = this.materials[ obj.uuid ];
      delete this.materials[ obj.uuid ];
    }
  }

  dispose() {
    if (this.environment) {
      this.environment.dispose();
    }
  }
}

export { StandardScene };
